import { lazy, Suspense, useEffect, type ReactElement } from "react";
import { CookieBanner } from "./components/CookieBanner";
import { CardCollectionProvider } from "./components/cards/CardCollection";
import { WhatsAppBubble } from "./components/WhatsAppBubble";
import { Link, Navigate, Route, Routes, useLocation, useParams } from "react-router";
import { marketPathFromCatalog } from "../shared/market";
import { CartDrawer } from "./components/CartDrawer";
import { MarketCartDrawer } from "./market/MarketCartDrawer";
import { VisitTracker } from "./market/visitSource";
import { CartSync } from "./cart/CartSync";
import { ComingSoonPage, ComingSoonStrip, PreviewStrip } from "./components/ComingSoon";
import { Footer } from "./components/Footer";
import { Header } from "./components/Header";
import { AccountPage } from "./pages/AccountPage";
import { ForgotPasswordPage } from "./pages/auth/ForgotPasswordPage";
import { LoginPage } from "./pages/auth/LoginPage";
import { RegisterPage } from "./pages/auth/RegisterPage";
import { ResetPasswordPage } from "./pages/auth/ResetPasswordPage";
import { CheckoutPage } from "./pages/CheckoutPage";
import { HomePage } from "./pages/HomePage";
import { IdentifyPage } from "./pages/IdentifyPage";
import { InstallPrompt } from "./components/InstallPrompt";
import { NotFoundPage } from "./pages/NotFoundPage";
import { ServerPage } from "./pages/ServerPage";
import { OrderPage } from "./pages/OrderPage";
import { ProductPage } from "./pages/ProductPage";
import { InfoPage } from "./pages/info/InfoPage";
import { INFO_PAGES } from "./pages/info/pages";
import { WithdrawalPage } from "./pages/WithdrawalPage";
import { UnsubscribePage } from "./pages/UnsubscribePage";
import { ShopEmailActionPage } from "./pages/ShopEmailActionPage";
import { useComingSoon, usePreview, useStoreSales } from "./lib/CatalogProvider";
import { useI18n } from "./i18n/I18nProvider";
import { MarketPage } from "./market/MarketPage";
import { SellPage } from "./pages/SellPage";
import { PlansPage } from "./pages/PlansPage";
import { BuyPage } from "./buy/BuyPage";
import { MyBuyRequests } from "./buy/MyBuyRequests";
import { ListingRedirect } from "./market/ListingRedirect";
import { SellerPage, ShortShopPage } from "./market/SellerPage";
import { ReservationPage } from "./market/Reservations";
import { MarketCheckoutPage } from "./market/MarketCheckoutPage";
import { MarketOrderPage } from "./market/MarketOrderPage";
import { LegacySellerRedirect, SellerApply, SellerDashboard } from "./market/SellerDashboard";
import { BuyerHub } from "./market/BuyerHub";
import { WantedPage } from "./wanted/WantedPage";
import { WantedCardPage } from "./wanted/WantedCardPage";
import { WantedForm } from "./wanted/WantedForm";
import { GameFromUrl } from "./components/GameSwitcher";

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    // A link to a section (#ofertas) scrolls there itself once the section has loaded.
    if (hash) return;
    // Block body: some browsers return a Promise from scrollTo(), which React would treat as a cleanup.
    window.scrollTo(0, 0);
  }, [pathname]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

/** Routes that need an account or money are closed while the store is "Próximamente". */
function Open({ children }: { children: ReactElement }) {
  return useComingSoon() ? <ComingSoonPage /> : children;
}

/** The store's checkout, while it sells its own stock; closed, a pointer to the market. */
function StoreSelling({ children }: { children: ReactElement }) {
  const { t } = useI18n();
  if (useStoreSales()) return children;
  return (
    <div className="container section">
      <p>{t("closed.checkout")}</p>
      <Link to="/mercado" className="btn btn-primary">
        {t("closed.heroCta")}
      </Link>
    </div>
  );
}

function LegacyPurchasesRedirect() {
  const { search, state } = useLocation();
  return <Navigate to={`/cuenta/compras${search}`} state={state} replace />;
}

function CatalogRedirect() {
  return <Navigate to={marketPathFromCatalog(useLocation().search)} replace />;
}

/** "/product/<slug>" (the English word) is the product's page; the server answers it with a 301 too. */
function EnglishProductPath() {
  const { slug = "" } = useParams();
  const { search, hash } = useLocation();
  return <Navigate to={`/producto/${encodeURIComponent(slug)}${search}${hash}`} replace />;
}

export function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      {/* The store's catalog is gone: old links open the market with the same search. */}
      <Route path="/tienda" element={<CatalogRedirect />} />
      <Route path="/producto/:slug" element={<ProductPage />} />
      <Route path="/product/:slug" element={<EnglishProductPath />} />
      <Route path="/identificar" element={<IdentifyPage />} />
      <Route path="/checkout" element={<Open><StoreSelling><CheckoutPage /></StoreSelling></Open>} />
      <Route path="/pedido/:number" element={<Open><OrderPage /></Open>} />
      <Route path="/cuenta" element={<Open><AccountPage /></Open>} />
      {/* Mi tienda moved to /mi-tienda: old links (emails, Mercado Pago's return) land on the same section. */}
      <Route path="/cuenta/vender" element={<LegacySellerRedirect />} />
      <Route path="/mi-tienda/*" element={<Open><SellerDashboard /></Open>} />
      <Route path="/cuenta/compras" element={<Open><BuyerHub /></Open>} />
      {/* Its old address (emails, pushes): same tab, same state. */}
      <Route path="/cuenta/mercado" element={<LegacyPurchasesRedirect />} />
      <Route path="/mercado" element={<MarketPage />} />
      <Route path="/vender" element={<SellPage />} />
      <Route path="/vender/planes" element={<PlansPage />} />
      <Route path="/vender/solicitar" element={<Open><SellerApply /></Open>} />
      <Route path="/se-busca" element={<WantedPage />} />
      <Route path="/se-busca/publicar" element={<Open><WantedForm /></Open>} />
      <Route path="/se-busca/:slug" element={<WantedCardPage />} />
      <Route path="/compramos" element={<Open><BuyPage /></Open>} />
      <Route path="/cuenta/compramos" element={<Open><MyBuyRequests /></Open>} />
      <Route path="/mercado/:id" element={<ListingRedirect />} />
      <Route path="/mercado/comprar/:handle" element={<Open><MarketCheckoutPage /></Open>} />
      <Route path="/mercado/pedido/:number" element={<Open><MarketOrderPage /></Open>} />
      <Route path="/vendedores/:handle" element={<SellerPage />} />
      <Route path="/reserva/:token" element={<ReservationPage />} />
      {/* A shop's short address, pokefans.fun/@handle (any other one-segment path is a 404). */}
      {/* Rendered by the server; here only when an old service worker answered it with the app. */}
      <Route path="/desarrolladores" element={<ServerPage />} />
      <Route path="/:segment" element={<ShortShopPage notFound={<NotFoundPage />} />} />
      {/* Sign-in stays reachable while "Próximamente": beta testers and admins log in here (the API refuses anyone else). */}
      <Route path="/ingresar" element={<LoginPage />} />
      <Route path="/registro" element={<RegisterPage />} />
      <Route path="/recuperar" element={<ForgotPasswordPage />} />
      <Route path="/restablecer" element={<ResetPasswordPage />} />
      {INFO_PAGES.map((p) => (
        <Route key={p.path} path={p.path} element={<InfoPage page={p} />} />
      ))}
      {/* "Botón de arrepentimiento": open even while "Próximamente" (it needs no account). */}
      <Route path="/arrepentimiento" element={<WithdrawalPage />} />
      <Route path="/baja" element={<UnsubscribePage />} />
      <Route path="/emails-tienda" element={<ShopEmailActionPage />} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

function SoonStrip() {
  const { pathname } = useLocation();
  const comingSoon = useComingSoon();
  const preview = usePreview();
  if (preview) return <PreviewStrip />;
  return comingSoon && pathname !== "/" ? <ComingSoonStrip /> : null;
}

// The back office is its own bundle: shoppers never download it.
const AdminApp = lazy(() => import("./admin/AdminApp"));

/** Layout shell. Providers live in main.tsx so tests can inject their own. */
export function App() {
  const { pathname } = useLocation();
  // Mi tienda has its own frame (menu, its header): no catalog header, cart or footer.
  if (pathname === "/mi-tienda" || pathname.startsWith("/mi-tienda/")) {
    return (
      <>
        <ScrollToTop />
        <GameFromUrl />
        <SoonStrip />
        <AppRoutes />
      </>
    );
  }
  if (pathname === "/admin" || pathname.startsWith("/admin/")) {
    return (
      <Suspense fallback={<div className="adm-center">Cargando…</div>}>
        <Routes>
          <Route path="/admin/*" element={<AdminApp />} />
        </Routes>
      </Suspense>
    );
  }
  return (
    <CardCollectionProvider>
      <ScrollToTop />
      <GameFromUrl />
      <VisitTracker />
      <a href="#main" className="skip-link">
        Skip to content
      </a>
      <Header />
      <SoonStrip />
      <main id="main">
        <AppRoutes />
      </main>
      <Footer />
      <CartDrawer />
      <MarketCartDrawer />
      <CartSync />
      <InstallPrompt />
      <WhatsAppBubble />
      <CookieBanner />
    </CardCollectionProvider>
  );
}
